import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../lib/axios';
import Button from '../../components/ui/Button';
import { Card, CardHeader, CardContent } from '../../components/ui/Card';
import { ArrowLeft, Target, ChevronDown, ChevronUp, Save, Loader2, AlertTriangle, Plus, Trash2, Edit3, Check, X } from 'lucide-react';

const strategyLevels = {
  1: { label: 'Conservative', color: '#22C55E', bg: 'rgba(34,197,94,0.1)' },
  2: { label: 'Moderate Conservative', color: '#84CC16', bg: 'rgba(132,204,22,0.1)' },
  3: { label: 'Moderate', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)' },
  4: { label: 'Moderate Aggressive', color: '#F97316', bg: 'rgba(249,115,22,0.1)' },
  5: { label: 'Aggressive', color: '#EF4444', bg: 'rgba(239,68,68,0.1)' }
};

const ManageInvestmentGoals = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [dirty, setDirty] = useState({}); // { 'q-<id>': true, 'a-<id>': true }
  const [editingField, setEditingField] = useState(null); // 'q-<id>' or 'a-<id>'
  const [editValue, setEditValue] = useState('');
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showAddAnswer, setShowAddAnswer] = useState({}); // { [questionKey]: boolean }
  const [newQuestion, setNewQuestion] = useState({ key: '', label: '', icon: 'Target', display_order: 999 });
  const [newAnswer, setNewAnswer] = useState({ question_key: '', value: '', label: '', display_order: 999 });
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => { fetchQuestions(); }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/investment-goal-rules/questions');
      setQuestions(response.data);
      const expanded = {};
      response.data.forEach(q => { expanded[q.key] = true; });
      setExpandedGroups(expanded);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // --- Inline editing ---

  const startEditing = (fieldKey, currentValue) => {
    setEditingField(fieldKey);
    setEditValue(currentValue);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue('');
  };

  const saveEdit = async (fieldKey) => {
    if (!editValue.trim()) return;
    const type = fieldKey.startsWith('q-') ? 'question' : 'answer';
    const id = parseInt(fieldKey.split('-')[1]);

    try {
      setSaving(fieldKey);
      if (type === 'question') {
        await api.put(`/investment-goal-rules/questions/${id}`, { label: editValue.trim() });
        setQuestions(prev => prev.map(q => q.id === id ? { ...q, label: editValue.trim() } : q));
      } else {
        await api.put(`/investment-goal-rules/answers/${id}`, { label: editValue.trim() });
        setQuestions(prev => prev.map(q => ({
          ...q,
          answers: q.answers.map(a => a.id === id ? { ...a, label: editValue.trim() } : a)
        })));
      }
      setEditingField(null);
      setEditValue('');
      setDirty(prev => { const u = { ...prev }; delete u[fieldKey]; return u; });
    } catch (error) {
      console.error('Failed to save:', error);
      setErrorMessage('Failed to save: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(null);
    }
  };

  // --- Override management ---

  const handleOverrideChange = (qIndex, aIndex, field, value) => {
    setQuestions(prev => {
      const updated = [...prev];
      const q = { ...updated[qIndex] };
      const answers = [...q.answers];
      const a = { ...answers[aIndex] };
      if (field === 'override_type') {
        a.override = a.override
          ? { ...a.override, override_type: value }
          : { id: null, override_type: value, strategy_level: 3, strategy_label: 'Moderate' };
      } else if (field === 'strategy_level') {
        const level = parseInt(value);
        const s = strategyLevels[level];
        a.override = a.override
          ? { ...a.override, strategy_level: level, strategy_label: s?.label || '' }
          : { id: null, override_type: 'max_level', strategy_level: level, strategy_label: s?.label || '' };
      }
      answers[aIndex] = a;
      q.answers = answers;
      updated[qIndex] = q;
      return updated;
    });
    const aId = questions[qIndex].answers[aIndex].id;
    setDirty(prev => ({ ...prev, [`o-${aId}`]: true }));
  };

  const saveOverride = async (qIndex, aIndex) => {
    const answer = questions[qIndex].answers[aIndex];
    const dirtyKey = `o-${answer.id}`;
    try {
      setSaving(dirtyKey);
      if (answer.override && answer.override.id !== null) {
        // Update existing rule
        await api.put(`/investment-goal-rules/${answer.override.id}`, {
          override_type: answer.override.override_type,
          strategy_level: answer.override.strategy_level
        });
      } else if (answer.override) {
        // Create new rule
        const result = await api.put(`/investment-goal-rules/answers/${answer.id}/override`, {
          override_type: answer.override.override_type,
          strategy_level: answer.override.strategy_level
        });
        // Update local state with the new rule id
        setQuestions(prev => prev.map((q, qi) => qi === qIndex ? {
          ...q,
          answers: q.answers.map((a, ai) => ai === aIndex ? { ...a, override: { ...a.override, id: result.data.id } } : a)
        } : q));
      }
      setDirty(prev => { const u = { ...prev }; delete u[dirtyKey]; return u; });
    } catch (error) {
      console.error('Failed to save override:', error);
      setErrorMessage('Failed to save override: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(null);
    }
  };

  const removeOverride = async (qIndex, aIndex) => {
    const answer = questions[qIndex].answers[aIndex];
    if (!answer.override) return;
    if (!confirm(`Remove override for "${answer.label}"?`)) return;

    try {
      if (answer.override.id) {
        await api.delete(`/investment-goal-rules/answers/${answer.id}/override`);
      }
      setQuestions(prev => prev.map((q, qi) => qi === qIndex ? {
        ...q,
        answers: q.answers.map((a, ai) => ai === aIndex ? { ...a, override: null } : a)
      } : q));
      const dirtyKey = `o-${answer.id}`;
      setDirty(prev => { const u = { ...prev }; delete u[dirtyKey]; return u; });
    } catch (error) {
      console.error('Failed to remove override:', error);
      setErrorMessage('Failed to remove override.');
    }
  };

  const addOverride = (qIndex, aIndex) => {
    setQuestions(prev => {
      const updated = [...prev];
      const q = { ...updated[qIndex] };
      const answers = [...q.answers];
      answers[aIndex] = { ...answers[aIndex], override: { id: null, override_type: 'max_level', strategy_level: 3, strategy_label: 'Moderate' } };
      q.answers = answers;
      updated[qIndex] = q;
      return updated;
    });
    const aId = questions[qIndex].answers[aIndex].id;
    setDirty(prev => ({ ...prev, [`o-${aId}`]: true }));
  };

  const handleSeedDefaults = async () => {
    if (!confirm('Reset questions & answers to defaults? Existing answers will update labels; overrides are not changed.')) return;
    try {
      await api.post('/investment-goal-rules/seed-questions');
      fetchQuestions();
    } catch (error) {
      setErrorMessage('Failed to seed questions.');
    }
  };

  const handleSeedRules = async () => {
    if (!confirm('Reset override rules to defaults? This adds missing rules but does not change existing ones.')) return;
    try {
      await api.post('/investment-goal-rules/seed');
      fetchQuestions();
    } catch (error) {
      setErrorMessage('Failed to seed rules.');
    }
  };

  // --- Create/Delete Question ---

  const handleAddQuestion = async () => {
    if (!newQuestion.key.trim() || !newQuestion.label.trim()) {
      alert('Key and label are required');
      return;
    }
    try {
      setSaving('new-question');
      const res = await api.post('/investment-goal-rules/questions', newQuestion);
      setQuestions(prev => [...prev, res.data]);
      setNewQuestion({ key: '', label: '', icon: 'Target', display_order: 999 });
      setShowAddQuestion(false);
      setSaving(null);
    } catch (error) {
      console.error('Failed to add question:', error);
      alert('Failed to add question: ' + (error.response?.data?.error || error.message));
      setSaving(null);
    }
  };

  const handleDeleteQuestion = async (questionKey, questionId) => {
    if (!confirm('Delete this question and all its answers? This cannot be undone.')) return;
    try {
      await api.delete(`/investment-goal-rules/questions/${questionId}`);
      setQuestions(prev => prev.filter(q => q.key !== questionKey));
    } catch (error) {
      console.error('Failed to delete question:', error);
      alert('Failed to delete question');
    }
  };

  // --- Create/Delete Answer ---

  const handleAddAnswer = async (questionKey) => {
    const answerData = { ...newAnswer, question_key: questionKey };
    if (!answerData.value.trim() || !answerData.label.trim()) {
      alert('Value and label are required');
      return;
    }
    try {
      setSaving(`new-answer-${questionKey}`);
      const res = await api.post('/investment-goal-rules/answers', answerData);
      setQuestions(prev => prev.map(q => {
        if (q.key === questionKey) {
          return { ...q, answers: [...q.answers, res.data] };
        }
        return q;
      }));
      setNewAnswer({ question_key: '', value: '', label: '', display_order: 999 });
      setShowAddAnswer(prev => { const u = { ...prev }; delete u[questionKey]; return u; });
      setSaving(null);
    } catch (error) {
      console.error('Failed to add answer:', error);
      alert('Failed to add answer: ' + (error.response?.data?.error || error.message));
      setSaving(null);
    }
  };

  const handleDeleteAnswer = async (answerId) => {
    if (!confirm('Delete this answer? This cannot be undone.')) return;
    try {
      await api.delete(`/investment-goal-rules/answers/${answerId}`);
      setQuestions(prev => prev.map(q => ({
        ...q,
        answers: q.answers.filter(a => a.id !== answerId)
      })));
    } catch (error) {
      console.error('Failed to delete answer:', error);
      alert('Failed to delete answer');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: theme.surface }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: theme.primary }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: theme.surface }}>
      <header className="border-b" style={{ backgroundColor: theme.card, borderColor: theme.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center flex-wrap gap-3">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: theme.text }}>
                <Target className="w-6 h-6" style={{ color: theme.primary }} />
                Investment Goals & Overrides
              </h1>
              <p className="text-sm" style={{ color: theme.textSecondary }}>
                {questions.length} questions • {questions.reduce((sum, q) => sum + q.answers.length, 0)} answers
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button variant="primary" onClick={() => setShowAddQuestion(true)}>+ Add Question</Button>
              <Button variant="ghost" onClick={() => navigate('/admin')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin Panel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {errorMessage && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{errorMessage}</p>}

        {/* Add Question Form */}
        {showAddQuestion && (
          <Card className="mb-6">
            <CardContent className="pt-4">
              <h3 className="text-lg font-semibold mb-3" style={{ color: theme.text }}>Add New Question</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: theme.textSecondary }}>Key (internal ID)</label>
                  <input
                    type="text"
                    value={newQuestion.key}
                    onChange={e => setNewQuestion({ ...newQuestion, key: e.target.value })}
                    placeholder="e.g., investment_objective"
                    className="w-full text-sm px-3 py-2 rounded-lg"
                    style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: theme.textSecondary }}>Display Order</label>
                  <input
                    type="number"
                    value={newQuestion.display_order}
                    onChange={e => setNewQuestion({ ...newQuestion, display_order: parseInt(e.target.value) || 999 })}
                    placeholder="999"
                    className="w-full text-sm px-3 py-2 rounded-lg"
                    style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                  />
                </div>
              </div>
              <div className="mb-3">
                <label className="text-xs font-medium mb-1 block" style={{ color: theme.textSecondary }}>Question Label</label>
                <input
                  type="text"
                  value={newQuestion.label}
                  onChange={e => setNewQuestion({ ...newQuestion, label: e.target.value })}
                  placeholder="e.g., What is your primary investment objective?"
                  className="w-full text-sm px-3 py-2 rounded-lg"
                  style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleAddQuestion}
                  disabled={saving === 'new-question'}
                >
                  {saving === 'new-question' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Add Question
                </Button>
                <Button variant="ghost" onClick={() => setShowAddQuestion(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Info banner */}
        <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: `${theme.primary}08`, border: `1px solid ${theme.primary}20` }}>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-2" style={{ color: theme.primary }}>
            <AlertTriangle className="w-4 h-4" /> How This Works
          </h3>
          <div className="text-xs space-y-1" style={{ color: theme.textSecondary }}>
            <p><strong>Edit labels:</strong> Click the ✏️ icon next to any question or answer to edit its text.</p>
            <p><strong>Force Strategy:</strong> This answer ALWAYS forces the selected strategy, regardless of risk score.</p>
            <p><strong>Max Level:</strong> Sets a ceiling — the recommendation cannot exceed this level.</p>
            <p><strong>No Override:</strong> Answer has no constraint — click "Add Override" to create one.</p>
          </div>
        </div>

        {/* Question groups */}
        <div className="space-y-4">
          {questions.map((question, qi) => (
            <Card key={question.key}>
              <CardHeader className="cursor-pointer" onClick={() => toggleGroup(question.key)}>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: `${theme.primary}15`, color: theme.primary }}>
                      <span className="text-sm font-bold">{question.display_order}</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      {editingField === `q-${question.id}` ? (
                        <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                          <input
                            type="text"
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveEdit(`q-${question.id}`); if (e.key === 'Escape') cancelEditing(); }}
                            className="flex-1 text-sm px-2 py-1 rounded"
                            autoFocus
                            style={{ backgroundColor: theme.surface, border: `1px solid ${theme.primary}`, color: theme.text }}
                          />
                          <button onClick={() => saveEdit(`q-${question.id}`)} className="p-1" style={{ color: '#22C55E' }}><Check className="w-4 h-4" /></button>
                          <button onClick={cancelEditing} className="p-1" style={{ color: '#EF4444' }}><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <h3 className="text-lg font-semibold truncate" style={{ color: theme.text }}>{question.label}</h3>
                      )}
                      <p className="text-xs" style={{ color: theme.textMuted }}>
                        Key: {question.key} • {question.answers.length} answers
                        {question.answers.filter(a => a.override).length > 0 && (
                          <span style={{ color: theme.primary }}> • {question.answers.filter(a => a.override).length} override{question.answers.filter(a => a.override).length !== 1 ? 's' : ''}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0" onClick={e => e.stopPropagation()}>
                    {editingField !== `q-${question.id}` && (
                      <button
                        onClick={() => startEditing(`q-${question.id}`, question.label)}
                        className="p-1.5 rounded-lg transition-colors"
                        style={{ color: theme.textSecondary }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = `${theme.primary}15`}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                        title="Edit question"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteQuestion(question.key, question.id)}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: '#EF4444' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(239,68,68,0.1)'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      title="Delete question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandedGroups[question.key] ? <ChevronUp className="w-5 h-5" style={{ color: theme.textSecondary }} /> : <ChevronDown className="w-5 h-5" style={{ color: theme.textSecondary }} />}
                  </div>
                </div>
              </CardHeader>

              {expandedGroups[question.key] && (
                <CardContent>
                  {/* Add Answer Form */}
                  {showAddAnswer[question.key] && (
                    <div className="mb-4 p-3 rounded-lg" style={{ backgroundColor: `${theme.primary}05`, border: `1px dashed ${theme.primary}30` }}>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
                        <div>
                          <label className="text-xs font-medium mb-1 block" style={{ color: theme.textSecondary }}>Answer Value (key)</label>
                          <input
                            type="text"
                            value={newAnswer.value}
                            onChange={e => setNewAnswer({ ...newAnswer, value: e.target.value })}
                            placeholder="e.g., growth_focused"
                            className="w-full text-sm px-2 py-1.5 rounded"
                            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium mb-1 block" style={{ color: theme.textSecondary }}>Display Order</label>
                          <input
                            type="number"
                            value={newAnswer.display_order}
                            onChange={e => setNewAnswer({ ...newAnswer, display_order: parseInt(e.target.value) || 999 })}
                            placeholder="999"
                            className="w-full text-sm px-2 py-1.5 rounded"
                            style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                          />
                        </div>
                      </div>
                      <div className="mb-2">
                        <label className="text-xs font-medium mb-1 block" style={{ color: theme.textSecondary }}>Answer Label</label>
                        <input
                          type="text"
                          value={newAnswer.label}
                          onChange={e => setNewAnswer({ ...newAnswer, label: e.target.value })}
                          placeholder="e.g., I want to maximize growth"
                          className="w-full text-sm px-2 py-1.5 rounded"
                          style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={() => handleAddAnswer(question.key)}
                          disabled={saving === `new-answer-${question.key}`}
                        >
                          {saving === `new-answer-${question.key}` ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
                          Add Answer
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setShowAddAnswer(prev => { const u = { ...prev }; delete u[question.key]; return u; })}>Cancel</Button>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    {question.answers.map((answer, ai) => (
                      <div
                        key={answer.id}
                        className="group flex items-center gap-3 p-3 rounded-lg"
                        style={{
                          backgroundColor: answer.override ? (answer.override.override_type === 'force_strategy' ? 'rgba(239,68,68,0.03)' : 'rgba(59,130,246,0.03)') : theme.surface,
                          border: `1px solid ${answer.override ? (answer.override.override_type === 'force_strategy' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)') : theme.border}`
                        }}
                      >
                        {/* Answer label (editable) */}
                        <div className="flex-1 min-w-0">
                          {editingField === `a-${answer.id}` ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveEdit(`a-${answer.id}`); if (e.key === 'Escape') cancelEditing(); }}
                                className="flex-1 text-sm px-2 py-1 rounded"
                                autoFocus
                                style={{ backgroundColor: theme.card, border: `1px solid ${theme.primary}`, color: theme.text }}
                              />
                              <button onClick={() => saveEdit(`a-${answer.id}`)} className="p-1" style={{ color: '#22C55E' }}><Check className="w-4 h-4" /></button>
                              <button onClick={cancelEditing} className="p-1" style={{ color: '#EF4444' }}><X className="w-4 h-4" /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <span className="text-sm" style={{ color: theme.text }}>{answer.label}</span>
                              <button
                                onClick={() => startEditing(`a-${answer.id}`, answer.label)}
                                className="p-1 rounded"
                                style={{ color: theme.textMuted, opacity: 0.4 }}
                                onMouseEnter={e => e.currentTarget.style.opacity = 1}
                                onMouseLeave={e => e.currentTarget.style.opacity = 0.4}
                                title="Edit answer"
                              >
                                <Edit3 className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Delete answer button */}
                        <button
                          onClick={() => handleDeleteAnswer(answer.id)}
                          className="p-1 rounded-lg"
                          style={{ color: '#EF4444' }}
                          title="Delete answer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>

                        {/* Override controls */}
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {answer.override ? (
                            <>
                              <select
                                value={answer.override.override_type}
                                onChange={e => handleOverrideChange(qi, ai, 'override_type', e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-lg"
                                style={{ backgroundColor: theme.card, border: `1px solid ${theme.border}`, color: theme.text }}
                              >
                                <option value="max_level">Max Level</option>
                                <option value="force_strategy">Force Strategy</option>
                              </select>
                              <select
                                value={answer.override.strategy_level}
                                onChange={e => handleOverrideChange(qi, ai, 'strategy_level', e.target.value)}
                                className="text-xs px-2 py-1.5 rounded-lg font-semibold"
                                style={{
                                  backgroundColor: strategyLevels[answer.override.strategy_level]?.bg || theme.card,
                                  border: `1px solid ${theme.border}`,
                                  color: strategyLevels[answer.override.strategy_level]?.color || theme.text
                                }}
                              >
                                {Object.entries(strategyLevels).map(([level, info]) => (
                                  <option key={level} value={level}>{info.label}</option>
                                ))}
                              </select>
                              {dirty[`o-${answer.id}`] && (
                                <button
                                  onClick={() => saveOverride(qi, ai)}
                                  disabled={saving === `o-${answer.id}`}
                                  className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium"
                                  style={{ backgroundColor: theme.primary, color: '#fff' }}
                                >
                                  {saving === `o-${answer.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                  Save
                                </button>
                              )}
                              <button
                                onClick={() => removeOverride(qi, ai)}
                                className="p-1 rounded-lg"
                                style={{ color: '#EF4444' }}
                                title="Remove override"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => addOverride(qi, ai)}
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium"
                              style={{ color: theme.primary, border: `1px dashed ${theme.primary}40`, backgroundColor: 'transparent' }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = `${theme.primary}08`}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              <Plus className="w-3 h-3" /> Add Override
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Add Answer button */}
                  {!showAddAnswer[question.key] && (
                    <button
                      onClick={() => setShowAddAnswer(prev => ({ ...prev, [question.key]: true }))}
                      className="w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                      style={{ color: theme.primary, border: `1px dashed ${theme.primary}30`, backgroundColor: 'transparent' }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = `${theme.primary}05`}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <Plus className="w-4 h-4" /> Add Answer Option
                    </button>
                  )}
                </CardContent>
              )}
            </Card>
          ))}
        </div>

        {questions.length === 0 && (
          <div className="text-center py-12">
            <Target className="w-12 h-12 mx-auto mb-3" style={{ color: theme.textMuted }} />
            <p style={{ color: theme.textSecondary }}>No questions configured.</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default ManageInvestmentGoals;
